import { memo, useState } from 'react'

import { BookmarkOne, Tag } from '@icon-park/react'

import { CreateThemeRequest, SearchResponse } from '@shared/models'

import { Tokens } from '@/assets/tokens'

import { Alert, Api } from '@/modules'

import { useInput, useRouting } from '@/hooks'

import { Button, Divider, Flex, Form, Input, Selector, Switch } from '@/components'
import { HorizontalTagInput, TagOption } from '@/components/core'
import { ManualCard } from '@/components/derived'

import { FullScreenModal } from '@/components/derived/FullScreenModal/FullScreenModal.component'
import { getFromToElements } from '@/utils/pagination'
import { useCompanyContext } from '../CompanyDetails.context'
import { getDescription } from '../utils'
import {
  FormContainer,
  PremiseCounter,
  PremiseHeader,
  PremiseLabel,
  PremiseSection,
  PremiseTextArea,
  TableDividerContainer,
  TableSectionContainer,
} from './Themes.styles'
import { ThemesTable } from './Themes.table'

export const Themes = memo(ThemesComponent)

function ThemesComponent() {
  const { companyID, changeQueryParam, currentPage } = useRouting()
  const { company, reloadCompany, reloadThemes, themes, themeFilters } = useCompanyContext()

  const { sendAlert } = Alert.Hook()

  const { value, binder: valueBinder, reset: resetValue } = useInput()
  const { binder: brandBinder, selectedOptions: selectedBrands, reset: resetBrands } = Selector.MultiBinder()
  const { selectedOption: selectedThemeGroup, binder: themeGroupBinder, reset: resetThemeGroup } = Selector.Binder()
  const strictDeterministicBinder = Switch.Hook()
  const [termsTags, setTermsTags] = useState<TagOption[]>([])
  const [detectionPremises, setDetectionPremises] = useState('')
  const [nonDetectionPremises, setNonDetectionPremises] = useState('')

  const [isSearchingBrands, , searchedBrands] = Api.Query<SearchResponse[]>(
    `brands/search?company=${companyID}&size=10000`,
  )
  const [isSearchingThemeGroups, searchThemeGroups, searchedThemeGroups = []] = Api.Query<SearchResponse[]>(
    `theme-groups/search?company=${companyID}&size=10000`,
  )
  const { isRunning: isCreatingTheme, run: createTheme } = Api.Command<void>(Api.Methods.POST, `themes`)

  function validatePremises(premises: string): boolean {
    if (!premises.trim()) return true
    const premisesList = premises.split(';').filter((p) => p.trim().length > 0)
    return premisesList.length <= 5
  }

  function onSubmit(): void {
    if (!strictDeterministicBinder.isChecked) {
      if (!validatePremises(detectionPremises)) {
        sendAlert({
          type: 'error',
          title: 'Erro de validação',
          message: 'Premissas de detecção: máximo de 5 premissas permitidas',
        })
        return
      }

      if (!validatePremises(nonDetectionPremises)) {
        sendAlert({
          type: 'error',
          title: 'Erro de validação',
          message: 'Premissas de não detecção: máximo de 5 premissas permitidas',
        })
        return
      }
    }

    const termsString = termsTags
      .map((tag) => tag.text)
      .filter((text) => text.trim() !== '')
      .join(';')

    const input = {
      companyID,
      value,
      brandIDs: selectedBrands.map((brand: { value: string | number; text: string }) => brand.value as string),
      themeGroupInternalID: selectedThemeGroup!.value,
      terms: termsString || undefined,
      isStrictDeterministic: strictDeterministicBinder.isChecked,
      detectionPremises: strictDeterministicBinder.isChecked ? undefined : detectionPremises.trim() || undefined,
      nonDetectionPremises: strictDeterministicBinder.isChecked ? undefined : nonDetectionPremises.trim() || undefined,
    } as CreateThemeRequest
    createTheme<CreateThemeRequest>({
      input,
      onSuccess,
    })
  }

  function onSuccess() {
    sendAlert({
      type: 'success',
      title: 'Sucesso',
      message: 'Tema cadastrado com sucesso!',
    })
    resetValue()
    resetBrands()
    resetThemeGroup()
    setTermsTags([])
    setDetectionPremises('')
    setNonDetectionPremises('')
    strictDeterministicBinder.reset?.()

    reloadThemes({
      page: currentPage,
      ...themeFilters,
    })
  }

  const detectionPremisesCount =
    detectionPremises.trim() ? detectionPremises.split(';').filter((p) => p.trim().length > 0).length : 0
  const nonDetectionPremisesCount =
    nonDetectionPremises.trim() ? nonDetectionPremises.split(';').filter((p) => p.trim().length > 0).length : 0

  if (!companyID || !company) return null

  return (
    <>
      <ManualCard
        icon={Tag}
        healthCheck={company.healthCheck?.isThemesChecked}
        title="Temas Monitorados"
        tooltip="Cadastre os termos específicos para rastreamento. Preencha o nome do tema e associe a uma marca e a um grupo já criados."
        description={getDescription(company.counts?.themes)}
        linkName="Editar"
        linkAction={() =>
          changeQueryParam({
            param: 'modal',
            value: 'temas',
            replaceAll: true,
          })
        }
      />
      <FullScreenModal
        controlKey="temas"
        backToName={company.name}
        title="Temas Monitorados"
        subtitle={
          <span>
            Preencha todos os campos <strong>obrigatórios (*)</strong> para concluir o cadastro do tema.
          </span>
        }
        icon={Tag}
        beforeClose={reloadCompany}>
        <FormContainer>
          <Form onSubmit={onSubmit} controlParam={isCreatingTheme}>
            <Flex $type="column" $align="stretch" $gap={24} $fit>
              <Input label="Nome do tema" placeholder="Nome do tema" required {...valueBinder} />

              <Flex $type="row" $justify="flex-start" $align="center" $gap={20}>
                <Selector.Multi
                  binder={brandBinder}
                  label="Marcas"
                  options={searchedBrands?.map((brand) => ({ text: brand.text, value: brand.value })) ?? []}
                  loading={isSearchingBrands}
                  required
                  placeholder="Selecione uma ou mais marcas"
                  $fit
                />
                <Selector.Searchable
                  {...themeGroupBinder}
                  label="Grupo do tema"
                  options={searchedThemeGroups.map((themeGroup) => ({
                    text: themeGroup.text,
                    value: themeGroup.value,
                  }))}
                  onSearch={(searchParam: string): void => {
                    searchThemeGroups({ name: searchParam })
                  }}
                  loading={isSearchingThemeGroups}
                  required>
                  <Selector.Icon value={BookmarkOne} color={Tokens.ColorUIPrimary} />
                </Selector.Searchable>
              </Flex>

              <HorizontalTagInput
                label="Termos (tags)"
                placeholder="Digite termos separados por ponto e vírgula (;)"
                maxCharacters={1024}
                onChange={setTermsTags}
                preSelectedTags={termsTags}
                showClearButton={true}
                showBooleanHelp={true}
              />

              <Switch.Root
                label="Aplicar apenas classificação determinística para este tema"
                {...strictDeterministicBinder}
              />

              {!strictDeterministicBinder.isChecked && (
                <Flex $type="column" $align="stretch" $gap={16} $fit>
                  <PremiseSection>
                    <PremiseHeader>
                      <PremiseLabel>Identificar TEMA quando: (opcional)</PremiseLabel>
                      <PremiseCounter>{detectionPremisesCount}/5 premissas</PremiseCounter>
                    </PremiseHeader>
                    <PremiseTextArea
                      placeholder="Digite as premissas separadas por ponto e vírgula (;) para quando detectar o tema"
                      value={detectionPremises}
                      onChange={(e) => setDetectionPremises(e.target.value)}
                      disabled={strictDeterministicBinder.isChecked}
                    />
                  </PremiseSection>

                  <PremiseSection>
                    <PremiseHeader>
                      <PremiseLabel>Não identificar TEMA nos casos a seguir: (opcional)</PremiseLabel>
                      <PremiseCounter>{nonDetectionPremisesCount}/5 premissas</PremiseCounter>
                    </PremiseHeader>
                    <PremiseTextArea
                      placeholder="Digite as premissas separadas por ponto e vírgula (;) para quando NÃO detectar o tema"
                      value={nonDetectionPremises}
                      onChange={(e) => setNonDetectionPremises(e.target.value)}
                      disabled={strictDeterministicBinder.isChecked}
                    />
                  </PremiseSection>
                </Flex>
              )}

              <Flex $type="row" $justify="flex-end" $align="center" $gap={20} $fit>
                <Button.Root type="submit" $variant="primary" $size={315} $loading={isCreatingTheme}>
                  <Button.Text value="Adicionar" />
                </Button.Root>
              </Flex>
            </Flex>
          </Form>
        </FormContainer>

        <TableSectionContainer>
          <TableDividerContainer>
            <Divider.Root>
              <Divider.Title value="Temas registrados" />
              <Divider.Spacer />
              <Divider.Subtitle
                value={`${getFromToElements(currentPage, themes?.elements.length ?? 0)}/${themes?.total ?? 0} Entrada(s)`}
              />
            </Divider.Root>
          </TableDividerContainer>
          <ThemesTable />
        </TableSectionContainer>
      </FullScreenModal>
    </>
  )
}
